import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Video, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Plus,
  Edit,
  Trash2,
  Grid3X3
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { VideoEntry } from "@/domain/rdo/schema";

type VideosProps = {
  value: VideoEntry[];
  onChange: (v: VideoEntry[]) => void;
  mode: "create" | "edit" | "view";
};

export function VideosModule({ value, onChange, mode }: VideosProps) {
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [editVideoOpen, setEditVideoOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<VideoEntry | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // autoplay muted por padrão
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  // Formulário para adicionar/editar vídeo
  const [formData, setFormData] = useState({
    url: "",
    caption: "",
  });

  const resetForm = () => {
    setFormData({ url: "", caption: "" });
  };

  const openPlayer = (index: number) => {
    setCurrentVideoIndex(index);
    setPlayerOpen(true);
    setIsPlaying(true);
  };

  const closePlayer = () => {
    setPlayerOpen(false);
    setIsPlaying(false);
  };

  const navigateVideo = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentVideoIndex(prev => prev === 0 ? value.length - 1 : prev - 1);
    } else {
      setCurrentVideoIndex(prev => prev === value.length - 1 ? 0 : prev + 1);
    }
    setIsPlaying(true);
  };

  const validateVideoUrl = (url: string): boolean => {
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi'];
    const extension = url.split('.').pop()?.toLowerCase();
    return videoExtensions.includes(extension || '');
  };

  const handleAddVideo = () => {
    if (!formData.url.trim()) {
      toast.error("URL do vídeo é obrigatória");
      return;
    }

    if (!validateVideoUrl(formData.url)) {
      toast.error("Formato de vídeo não suportado. Use MP4, WebM, OGG, MOV ou AVI");
      return;
    }

    const newVideo: VideoEntry = {
      id: crypto.randomUUID(),
      url: formData.url.trim(),
      caption: formData.caption.trim(),
      createdAt: new Date().toISOString(),
    };

    onChange([...value, newVideo]);
    setAddVideoOpen(false);
    resetForm();
    toast.success("Vídeo adicionado com sucesso");
  };

  const handleEditVideo = () => {
    if (!editingVideo) return;

    if (!formData.url.trim()) {
      toast.error("URL do vídeo é obrigatória");
      return;
    }

    if (!validateVideoUrl(formData.url)) {
      toast.error("Formato de vídeo não suportado. Use MP4, WebM, OGG, MOV ou AVI");
      return;
    }

    const updatedVideos = value.map(v => 
      v.id === editingVideo.id 
        ? { ...v, url: formData.url.trim(), caption: formData.caption.trim() }
        : v
    );

    onChange(updatedVideos);
    setEditVideoOpen(false);
    setEditingVideo(null);
    resetForm();
    toast.success("Vídeo atualizado com sucesso");
  };

  const handleDeleteVideo = (videoId: string) => {
    const updatedVideos = value.filter(v => v.id !== videoId);
    onChange(updatedVideos);
    toast.success("Vídeo removido com sucesso");
  };

  const openEditDialog = (video: VideoEntry) => {
    setEditingVideo(video);
    setFormData({
      url: video.url,
      caption: video.caption || "",
    });
    setEditVideoOpen(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!playerOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateVideo('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateVideo('next');
          break;
        case 'Escape':
          e.preventDefault();
          closePlayer();
          break;
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
      }
    };

    if (playerOpen) {
      document.addEventListener('keydown', handleKeyDown);
      playerRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [playerOpen, value.length]);

  const currentVideo = value[currentVideoIndex];

  return (
    <>
      <Card data-section="videos" className="print-no-break print-flat">
        <CardHeader className="pt-1 pb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Video className="w-4 h-4" />
              Vídeos ({value.length})
            </CardTitle>
            {mode !== "view" && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setAddVideoOpen(true)}
                className="text-xs"
              >
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="py-1 print-expand">
          {value.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 text-xs">
              Nenhum vídeo registrado
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {value.map((video, index) => (
                <div key={video.id} className="group cursor-pointer">
                  <div 
                    className="aspect-video bg-muted rounded-lg mb-1 flex items-center justify-center relative overflow-hidden border"
                    onClick={() => openPlayer(index)}
                  >
                    <video 
                      src={video.url}
                      className="w-full h-full object-cover"
                      muted
                      onLoadedData={(e) => {
                        // Capturar frame para thumbnail se não houver thumbUrl
                        if (!video.thumbUrl) {
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d');
                          const videoEl = e.target as HTMLVideoElement;
                          canvas.width = videoEl.videoWidth;
                          canvas.height = videoEl.videoHeight;
                          ctx?.drawImage(videoEl, 0, 0);
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Ações do vídeo */}
                    {mode !== "view" && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(video);
                          }}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVideo(video.id!);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {video.caption && (
                    <p className="font-medium text-xs line-clamp-2">{video.caption}</p>
                  )}
                  {video.createdAt && (
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(video.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Player Modal */}
      <Dialog open={playerOpen} onOpenChange={closePlayer}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none"
          ref={playerRef}
          tabIndex={-1}
        >
          <div className="relative w-full h-full min-h-[60vh] flex flex-col">
            {currentVideo && (
              <>
                {/* Close button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 z-20 text-white hover:bg-white/20"
                  onClick={closePlayer}
                  aria-label="Fechar player"
                >
                  <X className="w-5 h-5" />
                </Button>

                {/* Navigation buttons */}
                {value.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 z-20"
                      onClick={() => navigateVideo('prev')}
                      aria-label="Vídeo anterior"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 z-20"
                      onClick={() => navigateVideo('next')}
                      aria-label="Próximo vídeo"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </Button>
                  </>
                )}

                {/* Video player */}
                <div className="flex-1 relative">
                  <video 
                    ref={videoRef}
                    src={currentVideo.url}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay={isPlaying}
                    muted={isMuted}
                  />
                </div>

                {/* Video info */}
                <div className="bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      {currentVideo.caption && (
                        <p className="text-sm font-medium mb-1">{currentVideo.caption}</p>
                      )}
                      {currentVideo.createdAt && (
                        <p className="text-xs text-gray-300">
                          {format(new Date(currentVideo.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </p>
                      )}
                      {value.length > 1 && (
                        <p className="text-xs text-gray-400 mt-1">
                          {currentVideoIndex + 1} de {value.length}
                        </p>
                      )}
                    </div>
                    
                    {/* Custom controls */}
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pausar" : "Reproduzir"}
                      >
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={toggleMute}
                        aria-label={isMuted ? "Ativar som" : "Silenciar"}
                      >
                        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                        onClick={toggleFullscreen}
                        aria-label="Tela cheia"
                      >
                        <Maximize className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Video Modal */}
      <Dialog open={addVideoOpen} onOpenChange={(open) => {
        setAddVideoOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Vídeo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="video-url">URL do Vídeo *</Label>
              <Input
                id="video-url"
                placeholder="https://exemplo.com/video.mp4"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Formatos suportados: MP4, WebM, OGG, MOV, AVI
              </p>
            </div>
            
            <div>
              <Label htmlFor="video-caption">Legenda</Label>
              <Textarea
                id="video-caption"
                placeholder="Descrição ou legenda do vídeo..."
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setAddVideoOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddVideo}>
              Adicionar Vídeo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Video Modal */}
      <Dialog open={editVideoOpen} onOpenChange={(open) => {
        setEditVideoOpen(open);
        if (!open) {
          setEditingVideo(null);
          resetForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Vídeo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-video-url">URL do Vídeo *</Label>
              <Input
                id="edit-video-url"
                placeholder="https://exemplo.com/video.mp4"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Formatos suportados: MP4, WebM, OGG, MOV, AVI
              </p>
            </div>
            
            <div>
              <Label htmlFor="edit-video-caption">Legenda</Label>
              <Textarea
                id="edit-video-caption"
                placeholder="Descrição ou legenda do vídeo..."
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setEditVideoOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditVideo}>
              Salvar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}